// Ocean optics shared by the globe (earth.js) and rocketry's far water
// (terrain.js), so the patch's sea meets the globe's at its rim.
//
// public/textures/earth_ocean.webp (scripts/build-earth-ocean.mjs), loaded
// with NoColorSpace:
//   R  water coverage 0..1, lakes included, sea ice and ice shelves 0
//   G  R * ln(1 + depth m) / ln(11001)                 (ETOPO 2022)
//   B  R * ln(Kd490 / 0.01) / ln(600)                  (MODIS-Aqua climatology)
// G and B are premultiplied by R, so a filtered texel or a coarse mip averages
// only the water in it and G/R stays the water's own depth right up to the
// coast.
//
// Everything here is a reflectance: radiance times pi over the sun's
// irradiance on a level surface, per channel at 650, 550 and 470 nm. The
// caller scales it into its own albedo units.
//
// The water column (Maritorena, Morel and Gentili 1994):
//   R = Rinf + (A - Rinf) exp(-Kd z (1/mu_s,w + 1/mu_v,w))
// under the surface, then rho = 0.52 R / (1 - 1.7 R / pi) above it (Lee et
// al. 1998). Kd and the deep-water Rinf come from chlorophyll, which Kd490
// gives through Morel and Maritorena (2001). Shallow turbid seas (Yellow Sea,
// Rio de la Plata, the Gulf's banks) carry sediment the Case 1 model has no
// term for, so Rinf rises toward a sediment reflectance where Kd490 is high
// and the water is shallow enough to keep it stirred; high latitudes are left
// out because there a high Kd490 is dissolved organic matter, dark water
// (the Baltic).
//
// Constants fitted to the VIIRS depth bins of the Bahamas, the Great Barrier
// Reef lagoon and the Yellow Sea (Rayleigh-corrected reflectance): the bottom
// albedo, the sediment reflectance, the sky term, and EO_CLARITY. ETOPO's
// one-minute depth averages reef flats with the channels between them, and
// MODIS reads Kd490 high over a bright bottom, so taken at face value the
// model darkens the banks by 10 to 30 m; EO_CLARITY scales the green and blue
// attenuation of the bottom term to the depth that VIIRS shows it fading at.
// Red keeps pure water's own absorption.
export const EARTH_OCEAN_GLSL = /* glsl */`
// The globe's daylight scale on its albedo, and reflectance to that albedo
// for the sea (earth.js lights the sea with these; rocketry's far sea too).
const float EARTH_LIGHT = 1.65;
const float EARTH_OCEAN_ALBEDO = 1.0;
// The additive light the globe lays over its limb (earth.js, E4), 0.6 until
// the ocean pass: with the atmosphere shell's own glow over it, DSCOVR EPIC's
// clear ocean 65 to 85 degrees from nadir came out 2.2 to 2.9 times too
// bright. 0.4 takes a third of this term off and leaves the shell and the
// veil as they are.
const float EARTH_LIMB_HAZE = 0.4;
const vec3 EO_FLAT_SRGB = vec3(30.0, 59.0, 117.0);
// One texel of the ocean texture north to south (8192 x 4096 over the globe),
// in metres: the finest coast it can draw.
const float EO_MASK_TEXEL_M = 4886.0;
const vec3 EO_BOTTOM = vec3(0.22, 0.25, 0.29);
const vec3 EO_SEDIMENT = vec3(0.075, 0.12, 0.11);
const vec3 EO_CLARITY = vec3(1.0, 0.45, 0.45);
// Light off and just above the surface at nadir: sky light the surface
// reflects (Fresnel 0.02 of a blue sky) and the aerosol light VIIRS keeps
// over every sea, less the blue the callers' own Rayleigh veil already adds.
// Fitted with the veil in place to DSCOVR EPIC's clear ocean near nadir.
const vec3 EO_SKY = vec3(0.0075, 0.0085, 0.0075);
// Deep clear water at nadir with EO_SKY (chlorophyll 0.1): what the day map's
// flat ocean colour stands for, and that colour decoded to linear.
const vec3 EO_REFERENCE = vec3(0.0084, 0.0147, 0.0308);
const vec3 EO_FLAT_LINEAR = vec3(0.012983, 0.043735, 0.177888);

// How far a painted colour's departure from the flat sea carries: fitted so
// the Bahamas' painted banks land on VIIRS's 2 to 5 m reflectance.
const float EO_PAINTED_SCALE = 0.2;

struct EarthOcean { float cover; float depth; float kd490; };

// Sky light off the surface, growing with Fresnel toward grazing view.
vec3 earthSkyGlare(float muV) {
  float f = 1.0 - clamp(muV, 0.0, 1.0);
  float fresnel = 0.0204 + 0.9796 * f * f * f * f * f;
  return EO_SKY * min(fresnel / 0.0204, 3.0);
}

EarthOcean earthOceanDecode(vec3 o) {
  EarthOcean e;
  e.cover = o.r;
  float inv = 1.0 / max(o.r, 1.0 / 255.0);
  e.depth = pow(11001.0, clamp(o.g * inv, 0.0, 1.0)) - 1.0;
  e.kd490 = 0.01 * pow(600.0, clamp(o.b * inv, 0.0, 1.0));
  return e;
}

// 1 where the day map shows its one flat ocean colour, sRGB (30, 59, 117),
// fading out by 22 levels from it: water the map paints in its own colour
// (turquoise banks, sediment plumes) keeps that colour. The rule
// rocketry/earthfar.js reads the coastline with.
float earthOceanFlat(vec3 dayLinear) {
  vec3 c = clamp(dayLinear, 0.0, 1.0);
  vec3 s = mix(c * 12.92, 1.055 * pow(c, vec3(1.0 / 2.4)) - 0.055, step(0.0031308, c));
  return 1.0 - smoothstep(6.0, 22.0, distance(s * 255.0, EO_FLAT_SRGB));
}

// Above-water reflectance of the sea: the water column plus sky light off the
// surface. muS and muV are the sun's and the view's cosines to the vertical,
// absLatDeg the latitude's size in degrees.
vec3 earthWaterReflectance(float depth, float kd490, float absLatDeg, float muS, float muV) {
  float chl = clamp(pow(max(kd490 - 0.0166, 0.0) / 0.07242, 1.0 / 0.68955), 0.01, 30.0);
  vec3 kd = vec3(0.34052 + 0.04500 * pow(chl, 0.672),
                 0.05746 + 0.04111 * pow(chl, 0.649),
                 0.01251 + 0.08649 * pow(chl, 0.685));
  // Deep-water reflectance of the MM01 model (R = 0.33 bb/a, iterated), fitted
  // in log chlorophyll from 0.02 to 3 mg/m3.
  float x = log(chl) * 0.4342945;
  vec3 rinf = vec3(0.0034 * pow(chl, 0.3),
                   mix(0.0091, 0.0183, smoothstep(-1.9, 0.6, x)),
                   clamp(0.024 - 0.0197 * x, 0.008, 0.065));
  float sediment = smoothstep(0.08, 0.25, kd490) * (1.0 - smoothstep(20.0, 45.0, depth))
                 * (1.0 - smoothstep(50.0, 58.0, absLatDeg));
  rinf = mix(rinf, EO_SEDIMENT, sediment);
  // Slant path through the water: both rays refracted into it (n = 1.34).
  float msw = sqrt(1.0 - (1.0 - muS * muS) / 1.7956);
  float mvw = sqrt(1.0 - (1.0 - muV * muV) / 1.7956);
  vec3 R = rinf + (EO_BOTTOM - rinf) * exp(-kd * EO_CLARITY * depth * (1.0 / msw + 1.0 / mvw));
  return 0.52 * R / (1.0 - 1.7 * R / 3.14159265) + earthSkyGlare(muV);
}

// Water the day map paints in a colour of its own, carried into the same
// reflectance: its departure from the flat sea colour, scaled, on top of the
// deep clear water that colour stands for. Equal to that water where the map
// is flat, so the two meet without a seam across the flat ramp.
vec3 earthPaintedWater(vec3 dayLinear, float muV) {
  return max(EO_REFERENCE - EO_SKY + earthSkyGlare(muV) + (dayLinear - EO_FLAT_LINEAR) * EO_PAINTED_SCALE, vec3(0.0));
}

// Sun glint (Cox and Munk 1954): the facets tilted to mirror the sun into the
// view, with the slope variance a wind of speed windMs raises. Returned as a
// reflectance times mu_s, the form a Lambert albedo takes once lit, so it
// adds to the lit albedo directly:
//   rho_g mu_s = pi F p(tan beta) / (4 mu_v cos^4 beta)
// Nothing in it depends on distance: the spot is as broad as the slopes make
// it from any range.
float earthSlopeVariance(float windMs) { return 0.003 + 0.00512 * windMs; }
// The same with the facets' slope variance given directly: close up the
// resolved waves tilt N and only the variance they leave unresolved is in s2.
float earthGlintS2(vec3 N, vec3 L, vec3 V, float s2) {
  float muS = dot(N, L);
  float muV = dot(N, V);
  if (muS <= 0.0 || muV <= 0.0) return 0.0;
  vec3 Hv = normalize(L + V);
  float cb = max(dot(N, Hv), 1e-3);
  float cb2 = cb * cb;
  float tan2 = (1.0 - cb2) / cb2;
  float p = exp(-tan2 / s2) / (3.14159265 * s2);
  float f = 1.0 - clamp(dot(V, Hv), 0.0, 1.0);
  float F = 0.0204 + 0.9796 * f * f * f * f * f;
  return 3.14159265 * F * p / (4.0 * max(muV, 0.08) * cb2 * cb2) * smoothstep(0.0, 0.04, muS);
}
float earthGlint(vec3 N, vec3 L, vec3 V, float windMs) {
  return earthGlintS2(N, L, V, earthSlopeVariance(windMs));
}

// Land albedo, calibrated against DSCOVR EPIC's full disc (Sahara, Amazon,
// boreal forest). The day map's bright soils were stretched for display: the
// Sahara sits at (0.72, 0.56, 0.41), twice its reflectance and far paler
// (MODIS reads about 0.42, 0.30, 0.18), while its forests are close to their
// own. Bright coloured ground takes a power curve that restores the soil's
// red-to-blue ratio and a scale back to its reflectance; dark ground keeps
// the map's colour. Snow, ice and salt pans carry little colour, and the
// curve leaves them be.
const float LAND_SOIL_GAMMA = 2.0;
const float LAND_SOIL_SCALE = 0.5;
vec3 earthLandAlbedo(vec3 day) {
  float hi = max(day.r, max(day.g, day.b));
  float lo = min(day.r, min(day.g, day.b));
  float tint = smoothstep(0.08, 0.2, (hi - lo) / max(hi, 1e-3));
  float w = smoothstep(0.06, 0.4, dot(day, vec3(0.2126, 0.7152, 0.0722))) * tint;
  return mix(day, pow(max(day, 0.0), vec3(LAND_SOIL_GAMMA)) * LAND_SOIL_SCALE, w);
}

// The same up close, where the streamed imagery resolves single roofs, salt
// flats and car parks. earthLandAlbedo leaves colourless ground as it is and
// takes coloured ground down by half and more, so across the narrow band of
// colour between the two a pixel of the same brightness came out three times
// brighter or darker: grey town and white sabkha stood as hard-edged white
// blotches on the darkened sand. Its brightness is not monotonic either
// (ground at 0.2 came out brighter than ground at 0.4), which drew dark rims
// round fields and, with the squared colour, pushed towns to rust. The
// streamed pages are one photograph, land and water, exposed together, so
// up close they keep their own colour and contrast: each pixel scaled by one
// smooth factor of its brightness, from 1 for dark ground (as earthLandAlbedo
// leaves it) to 0.37 for bright, which lands bright sand where
// earthLandAlbedo puts it and never turns back (the curve rises
// everywhere). Ground both bright and colourless
// (snow, ice, salt, white roofs, where the photograph clips) keeps nearly
// its own light, as much brighter than sand as it really is. The globe
// blends to this as the pixel shrinks from 2 to 0.8 km (earth.js), so from
// afar earthLandAlbedo, and the EPIC fit, are unchanged.
vec3 earthLandAlbedoClose(vec3 day) {
  vec3 c = max(day, 0.0);
  float hi = max(c.r, max(c.g, c.b));
  float lo = min(c.r, min(c.g, c.b));
  float chroma = (hi - lo) / max(hi, 1e-3);
  float lum = dot(c, vec3(0.2126, 0.7152, 0.0722));
  float white = smoothstep(0.82, 0.95, lum) * (1.0 - smoothstep(0.04, 0.12, chroma));
  return c * mix(0.37 + 0.63 * exp(-lum / 0.15), 0.9, white);
}

// How surely a streamed imagery colour (linear) is water, 0 to 1: the globe
// draws the coast from this up close, where the ocean texture's 5 km texels
// put it kilometres off (earth.js). Water is the ground whose blue stands
// above its red: deep water by far, shallow banks and turquoise lagoons by
// less, while sand, rock, fields, forest and towns all hold red at or above
// blue. Shaded snow and blue ice are as blue as water, but bright, bluer
// than green and with the red of skylight in them; that is taken off (teal
// water as bright, its blue level with its green, keeps its answer). prior
// is the ocean texture's own coverage here: it moves the threshold toward
// its answer, a little for bright ground (banks as pale as the beach still
// count as sea where the texture says sea) and more for dark, where the
// photograph's colour is a few levels of JPEG noise and a channel's deep
// water would otherwise break into blocks of land.
float earthImageryWater(vec3 c, float prior) {
  float hi = max(c.r, max(c.g, c.b));
  float blue = (c.b - c.r) / max(hi, 0.03);
  float lum = dot(c, vec3(0.2126, 0.7152, 0.0722));
  float icy = smoothstep(0.06, 0.18, lum) * smoothstep(0.04, 0.12, (c.b - c.g) / max(hi, 0.004))
            * smoothstep(0.25, 0.4, c.r / max(c.b, 0.004));
  float dark = 1.0 - smoothstep(0.02, 0.06, lum);
  return smoothstep(0.02, 0.14, blue - icy + (prior - 0.5) * (0.2 + 0.4 * dark));
}

// The clear air's own light between a camera and the ground under it (the
// globe's veil up close, earth.js): single Rayleigh scattering of the sun in
// a plane-parallel layer holding the share column of the air, toward a
// view muV from the vertical, the sun muS from it, at the scattering angle
// whose cosine is cosScatter. In the lit albedo's units before EARTH_LIGHT
// (a reflectance times mu_s). The layer's own extinction bounds it toward
// the horizon. Optical depths of the whole column at 650, 550 and 470 nm
// (Bodhaine et al. 1999).
const vec3 EO_RAYLEIGH_TAU = vec3(0.050, 0.097, 0.184);
vec3 earthRayleighPath(float column, float muS, float muV, float cosScatter) {
  float phase = 0.75 * (1.0 + cosScatter * cosScatter);
  return phase * 0.25 * muS / (muS + muV) * (1.0 - exp(-EO_RAYLEIGH_TAU * column * (1.0 / muS + 1.0 / muV)));
}
// The ground's light through the same layer, up to the camera.
vec3 earthRayleighTransmit(float column, float muV) {
  return exp(-EO_RAYLEIGH_TAU * column / muV);
}

// The globe's daylight (earth.js), for callers that have to meet it: the sky's
// ambient light, blue under a high sun and amber under a low one, normalised
// to unit luminance so it sets the hue only; and the sun's own colour after
// the air it came through (Kasten-Young air mass, relative to the zenith).
vec3 earthSkyTint(float muS) {
  vec3 t = mix(vec3(1.00, 0.62, 0.36), vec3(0.48, 0.66, 1.00), smoothstep(-0.05, 0.32, muS));
  return t / max(dot(t, vec3(0.2126, 0.7152, 0.0722)), 1e-4);
}
vec3 earthSunThroughAir(float muS) {
  float airMass = 1.0 / max(muS + 0.16, 0.16);
  return exp(-vec3(0.04, 0.14, 0.40) * max(airMass - 1.0, 0.0) * 0.50);
}
// The glint is the sun's direct beam twice through the air, down and back up
// to the camera, so Rayleigh takes its blue where the diffuse ground gets it
// back from the sky: warm silver from high above, gold toward the limb. The
// down leg's slant is earthSunThroughAir's; this is a zenith crossing down
// plus the view's leg, relative to the green of a clear vertical pair.
vec3 earthGlintTint(float muV) {
  return exp(-vec3(0.04, 0.09, 0.24) * (1.0 + 1.0 / max(muV, 0.08)) + 0.18);
}

// A hash and value noise of its own, so every caller reads the same wind.
// The lattice wraps east-west after period cells, a whole number of them
// round the globe, so the field is continuous across the date line.
float eoHash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123); }
float eoNoise(vec2 p, float period) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  float x0 = mod(i.x, period), x1 = mod(i.x + 1.0, period);
  return mix(mix(eoHash(vec2(x0, i.y)), eoHash(vec2(x1, i.y)), u.x),
             mix(eoHash(vec2(x0, i.y + 1.0)), eoHash(vec2(x1, i.y + 1.0)), u.x), u.y);
}

// Surface wind over the sea in m/s at map address uv, about 7 m/s on
// average: synoptic swells of stronger and lighter wind, with gusty streaks
// and calm slicks inside them. Each scale fades out once a pixel (pixM,
// metres) spans a good part of its cells, so a distant sea reads the mean
// and nothing shimmers as the view moves. Cells run four times longer east
// than north at the equator (u spans twice the angle v does), along the
// trades and the westerlies.
float earthOceanWind(vec2 uv, float pixM) {
  float synoptic = eoNoise(uv * vec2(45.0, 90.0), 45.0) - 0.5;
  float gusts = eoNoise(uv * vec2(130.0, 260.0) + vec2(0.0, 1.3), 130.0) - 0.5;
  float slicks = eoNoise(uv * vec2(350.0, 700.0) + vec2(0.0, 5.9), 350.0) - 0.5;
  float w = 1.0 + synoptic * 0.9
          + gusts * 0.6 * (1.0 - smoothstep(12000.0, 40000.0, pixM))
          + slicks * 0.5 * (1.0 - smoothstep(4000.0, 14000.0, pixM));
  return 7.0 * clamp(w, 0.25, 1.9);
}
`;

// Waves up close (oceanwaves.js): an FFT wave field's slopes and whitecaps in
// a two-layer array, mapped onto the sphere through the three planes of the
// planet's frame. Coordinates are metres from an anchor near the camera plus
// that anchor's phase in the field's 25,600 m period (P, with its screen
// derivatives taken by the caller outside any branch), so they hold to
// millimetres at the viewer's closest. Each octave of the waves' slope
// variance that the pixel cannot resolve goes back into the glint's lobe, so
// from orbit the result is the plain Cox-Munk sea above. Globe only (earth.js).
export const EARTH_OCEAN_WAVES_GLSL = /* glsl */`
uniform float uWavesOn;
uniform vec3 uWavePhase;
uniform float uWaveScale;
uniform mat3 uWaveObjToWorld;
uniform highp sampler2DArray uWaveField;
uniform float uWaveOctaveShare[12];
uniform float uWaveResolvedShare;
uniform float uCameraAltitudeM;
varying vec3 vWaveRel;
varying vec3 vWaveObjN;

struct SeaWaves { vec3 n; float s2; float resolved; float foam; vec2 cell; float fp; };

// The share of the air column that lies between the camera and the ground
// (8 km scale height): 1 from orbit, about a fifth from 2 km up, where the
// orbital veil would otherwise grey the whole view.
float earthAirBelowCamera() { return 1.0 - exp(-max(uCameraAltitudeM, 0.0) / 8000.0); }

// The swell patch is sampled twice, the second time turned 16 degrees by the
// 7-24-25 triangle (whole multiples of 1,024 m across the 25,600 m period),
// and the two blended slowly so its kilometre repeat never lines up while
// the sea keeps one main direction.
const mat2 OW_TURN = mat2(0.96, -0.28, 0.28, 0.96);

void earthWavePlane(vec2 c, vec2 cdx, vec2 cdy, float wind, inout vec2 slope, inout float foam) {
  float mixA = clamp(0.5 + 0.25 * (sin(6.28318530718 * c.x / 2560.0) + sin(6.28318530718 * c.y / 1706.6667 + 1.3)), 0.0, 1.0);
  vec2 c0 = c / 1024.0, d0x = cdx / 1024.0, d0y = cdy / 1024.0;
  vec2 c1 = c / 128.0, d1x = cdx / 128.0, d1y = cdy / 128.0;
  vec4 s0 = textureGrad(uWaveField, vec3(c0, 0.0), d0x, d0y);
  vec4 s0t = textureGrad(uWaveField, vec3(OW_TURN * c0, 0.0), OW_TURN * d0x, OW_TURN * d0y);
  vec4 s1 = textureGrad(uWaveField, vec3(c1, 0.0), d1x, d1y);
  vec4 f0 = textureGrad(uWaveField, vec3(c0, 1.0), d0x, d0y);
  vec4 f1 = textureGrad(uWaveField, vec3(c1, 1.0), d1x, d1y);
  // The turned sample's gradient back in this plane's axes is M^T grad.
  slope += sqrt(mixA) * s0.xy + sqrt(1.0 - mixA) * (transpose(OW_TURN) * s0t.xy) + s1.zw;
  foam += mixA * f0.x + f1.y;
}

SeaWaves earthSeaWaves(vec3 P, vec3 dPdx, vec3 dPdy, vec3 baseNormal, float windMs) {
  SeaWaves w;
  float s2Total = earthSlopeVariance(windMs);
  // Pixel footprint on the sea in metres, the larger axis, as the mip
  // chain filters.
  float fp = max(max(length(dPdx), length(dPdy)), 1e-3);
  float unresolved = 0.0;
  for (int j = 0; j < 12; j++) {
    float lambda = 2048.0 / exp2(float(j) + 0.5);
    unresolved += uWaveOctaveShare[j] * (1.0 - smoothstep(1.5, 4.0, lambda / fp));
  }
  w.s2 = s2Total * (1.0 - uWaveResolvedShare * (1.0 - unresolved));
  w.resolved = smoothstep(0.05, 0.45, 1.0 - unresolved);
  w.n = baseNormal;
  w.foam = 0.0;
  w.fp = fp;
  vec3 nObj = normalize(vWaveObjN);
  vec3 tw = pow(abs(nObj), vec3(8.0));
  tw /= tw.x + tw.y + tw.z;
  // The dominant plane's coordinates, for the glitter's cells.
  w.cell = tw.x >= max(tw.y, tw.z) ? P.zy : (tw.y >= tw.z ? P.xz : P.xy);
  if (w.resolved <= 0.0) return w;
  vec3 pert = vec3(0.0);
  float foam = 0.0;
  if (tw.x > 0.01) {
    vec2 g = vec2(0.0); float f = 0.0;
    earthWavePlane(P.zy, dPdx.zy, dPdy.zy, windMs, g, f);
    pert -= tw.x * (g.x * vec3(0.0, 0.0, 1.0) + g.y * vec3(0.0, 1.0, 0.0));
    foam += tw.x * f;
  }
  if (tw.y > 0.01) {
    vec2 g = vec2(0.0); float f = 0.0;
    earthWavePlane(P.xz, dPdx.xz, dPdy.xz, windMs, g, f);
    pert -= tw.y * (g.x * vec3(1.0, 0.0, 0.0) + g.y * vec3(0.0, 0.0, 1.0));
    foam += tw.y * f;
  }
  if (tw.z > 0.01) {
    vec2 g = vec2(0.0); float f = 0.0;
    earthWavePlane(P.xy, dPdx.xy, dPdy.xy, windMs, g, f);
    pert -= tw.z * (g.x * vec3(1.0, 0.0, 0.0) + g.y * vec3(0.0, 1.0, 0.0));
    foam += tw.z * f;
  }
  // The seas build with the wind: slopes with Cox-Munk's variance, whitecap
  // cover roughly with its cube (Monahan), none in a calm.
  float windAmp = sqrt(s2Total / earthSlopeVariance(7.0));
  pert -= nObj * dot(pert, nObj);
  w.n = normalize(baseNormal + uWaveObjToWorld * (pert * windAmp * w.resolved));
  // Whitecaps only where their patches are resolved: from further off they
  // are part of the sea's fitted reflectance already.
  w.foam = clamp(foam * clamp(pow(windMs / 8.0, 3.4), 0.0, 3.0), 0.0, 1.0) * (1.0 - smoothstep(6.0, 40.0, fp));
  return w;
}

// Glitter. A pixel of sea holds countless facets smaller than itself, and
// the sun shows in the few tilted just so: many in the middle of the glint,
// a scattered handful at its edges, which is what makes water sparkle where
// a smooth lobe reads as polished stone. The count in a cell of sea about a
// pixel across is drawn from Poisson with the mean the lobe gives (density
// times area times the slope density p that mirrors the sun), and the glint
// is scaled by count over mean, so its average stays Cox-Munk's. Cells sit
// on the sea in metres (a hash that wraps with the field's period, so the
// pattern holds as the anchor moves), cross-fade between power-of-two sizes
// as the footprint changes, and each twinkles on its own clock with smooth
// fades. Far off a cell holds thousands of glints and this is 1.
const float GLITTER_DENSITY = 0.6;
float eoHash3(vec3 p) {
  p = fract(p * vec3(0.1031, 0.1030, 0.0973));
  p += dot(p, p.yxz + 33.33);
  return fract((p.x + p.y) * p.z);
}
float eoPoisson(float lambda, float u, float v) {
  if (lambda > 12.0) {
    float g = sqrt(-2.0 * log(max(u, 1e-6))) * cos(6.28318530718 * v);
    return max(lambda + sqrt(lambda) * g, 0.0);
  }
  float pk = exp(-lambda);
  float cdf = pk;
  float k = 0.0;
  for (int i = 0; i < 24; i++) {
    if (u <= cdf) break;
    k += 1.0;
    pk *= lambda / k;
    cdf += pk;
  }
  return k;
}
float earthGlitterLevel(vec2 c, float cell, float lambda, float t) {
  float wrap = 25600.0 / cell;
  vec2 id = mod(floor(c / cell), wrap);
  float phase = t * 1.2 + eoHash3(vec3(id, cell)) * 11.0;
  float epoch = floor(phase);
  float fade = smoothstep(0.0, 1.0, fract(phase));
  float a = eoPoisson(lambda, eoHash3(vec3(id, epoch + cell * 0.37)), eoHash3(vec3(id.yx, epoch + 5.1)));
  float b = eoPoisson(lambda, eoHash3(vec3(id, epoch + 1.0 + cell * 0.37)), eoHash3(vec3(id.yx, epoch + 6.1)));
  return mix(a, b, fade) / max(lambda, 1e-4);
}
// p: the slope density at the facet tilt that mirrors the sun into view.
float earthGlitter(SeaWaves w, float p, float t) {
  float lod = log2(max(w.fp, 0.25));
  float l0 = floor(lod);
  float cellA = exp2(l0);
  float cellB = cellA * 2.0;
  float ga = earthGlitterLevel(w.cell, cellA, GLITTER_DENSITY * cellA * cellA * p, t);
  float gb = earthGlitterLevel(w.cell, cellB, GLITTER_DENSITY * cellB * cellB * p, t);
  return min(mix(ga, gb, fract(lod)), 40.0);
}
// The slope density at the mirror tilt, as earthGlintS2 evaluates it.
float earthGlintDensity(vec3 N, vec3 L, vec3 V, float s2) {
  vec3 Hv = normalize(L + V);
  float cb = max(dot(N, Hv), 1e-3);
  float tan2 = (1.0 - cb * cb) / (cb * cb);
  return exp(-tan2 / s2) / (3.14159265 * s2);
}

// The sky the wave facets mirror, in the globe's lit-albedo units (a white
// level surface under a zenith sun reads EARTH_LIGHT): pale and bright at the
// horizon, deeper blue overhead, with the daylight.
vec3 earthReflectedSky(float sinElevation, float muS) {
  float t = sqrt(clamp(sinElevation, 0.0, 1.0));
  vec3 sky = oklchMix(vec3(0.30, 0.34, 0.40), vec3(0.06, 0.11, 0.24), t);
  float day = smoothstep(-0.08, 0.25, muS) * (0.35 + 0.65 * sqrt(max(muS, 0.0)));
  return sky * day * EARTH_LIGHT;
}
`;
